import cn from "classnames"
import React, { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import videojs from "video.js"
import "video.js/dist/video-js.css"
import type Player from "video.js/dist/types/player"
import ClientData from "@/common/client_data"
import Scrollable from "@/common/components/scrollable"
import env from "@/common/env"
import { sizeUnit } from "@/common/locales"
import { isTablet } from "@/common/util"
import Icons from "@/images/signomatic/fixmethods"
import { currentPresetSettings, optionForcedState } from "@/signomatic/app/sign_validator"
import SelectedIndicator from "@/signomatic/components/selected_indicator"
import Toolbox from "@/signomatic/components/toolbox"
import { type ToolboxStripItemProps } from "@/signomatic/components/toolbox_strip"
import { actions } from "@/signomatic/store/actions"
import { type State } from "@/signomatic/store/reducers"

export const description = (type: string, signType: string, material: string): string => {
  if (type === "none") {
    return ""
  }

  const plastic =
    ["plastic_sign", "contour_cut", "tactile_sign"].includes(signType) ||
    (signType === "label_plates" && material === "regular")

  if (["self_adhesive_pad", "tape"].includes(type)) {
    if (plastic) {
      type += "_plastic"
    } else if (
      [
        "forex_sign",
        "solid_plastic_sign",
        "aluminium_sign",
        "label_plates",
        "brass_sign",
        "wood_sign",
        "ecoboard",
        "stainless_steel_sign",
      ].includes(signType)
    ) {
      type += "_alu"
    }
  }

  const labelplateBrass = signType === "label_plates" && material === "brass"

  if (type === "tape_and_screw") {
    if (labelplateBrass) {
      type += "_brass"
    }
  }

  if (type === "magnet" && signType === "magnetic_sign") {
    type = "magnet_magnetic"
  }

  if (isTablet()) {
    type += "_short"
  }

  return `fixmethod_toolbox.${type}`
}

export const reason = (type: string): string => {
  switch (type) {
    case "shape":
      return "toolbox.fix_method_disabled_shape"
    case "size":
      return "toolbox.fix_method_disabled_size"
    case "color":
      return "toolbox.fix_method_disabled_color"
    case "thickness":
      return "toolbox.fix_method_disabled_thickness"
    default:
      return ""
  }
}

const media = (signType: string, key: string): { video: string | null; image: string | null; text: string | null } => {
  const sign = ClientData.signs?.find(s => s.key === signType)
  const current = sign && sign.fixmethods ? sign.fixmethods.find(method => method.key === key) : null

  if (!current) {
    return { video: null, image: null, text: null }
  }

  current.text = ClientData.toolboxSnippet(key)

  return current
}

interface VideoProps {
  src: string
}

const Video: React.FC<VideoProps> = ({ src }) => {
  const videoRef = useRef(null)

  useEffect(() => {
    let player: Player
    if (videoRef.current) {
      player = videojs(videoRef.current, {
        controls: true,
        preload: "auto",
        fluid: true,
      })
    }

    return () => {
      if (player) {
        player.dispose()
      }
    }
  }, [])

  return (
    <div className="video-wrapper">
      <video ref={videoRef} className="video-js vjs-default-skin">
        <source src={src} type="video/mp4" />
      </video>
    </div>
  )
}

interface FixmethodDetailsProps {
  image?: string | null
  video?: string | null
  text?: string | null
}

const FixmethodDetails: React.FC<FixmethodDetailsProps> = ({ image, video, text }) => (
  <div className="described-details fixmethod">
    {image && <img src={image} alt="" />}
    {video && <Video key={video} src={video} />}
    {text && <div className="description" dangerouslySetInnerHTML={{ __html: text }} />}
  </div>
)

interface FixmethodProps {
  name: string
  description: string
  disabledReason: string
  enabled: boolean
  active: boolean
  image: boolean
  video: boolean
  text: boolean
  update: () => void
  onReadMore: (e: React.MouseEvent) => void
}

const Fixmethod = ({
  name,
  description,
  enabled,
  active,
  image,
  video,
  text,
  disabledReason,
  onReadMore,
  update,
}: FixmethodProps) => {
  const { t } = useTranslation()

  return (
    <div className={cn("described", "selection", `fixmethod-${name}`, { disabled: !enabled, active })} onClick={update}>
      {!enabled && (
        <div className={cn("disabled-reason paragraph-2xs bold", disabledReason)}>{t(reason(disabledReason))}</div>
      )}

      <div className="contents">
        <div className="image">{Icons[name] ? React.createElement(Icons[name]) : ""}</div>
        <div className={cn("details", { center: !description })}>
          <h5 className="mb-0" dangerouslySetInnerHTML={{ __html: t(`fixmethod.${name}`) }} />
          <p className="paragraph-2xs mb-0">{description}</p>
          {description ? (
            <>
              {["pole", "pole_flanged"].includes(name) && (
                <p className="paragraph-2xs">
                  <br />
                  {env.locale == "en-GB" ? "(Ø 76 mm)" : `(Ø ${sizeUnit === "in" ? "2-2.5" : "50–65"} ${sizeUnit}) `}
                </p>
              )}
              {image || video || text ? (
                <div className="info-row">
                  <p className="paragraph-xs mb-0">
                    <a onClick={onReadMore} className={cn({ video, image, text })}>
                      {video && t("toolbox.see_video")}
                      {image && t("toolbox.see_image")}
                      {text && t("misc.readmore")}
                      ...
                    </a>
                  </p>
                  <SelectedIndicator active={active} />
                </div>
              ) : (
                <div className="info-row right">
                  <SelectedIndicator active={active} />
                </div>
              )}
            </>
          ) : (
            <SelectedIndicator active={active} />
          )}
        </div>
      </div>
    </div>
  )
}

interface FixmethodToolboxStateProps {
  data: SignData
  signType: string
  signState: SignState
  subToolbox: string
}

interface FixmethodToolboxDispatchProps {
  setSubToolbox: typeof actions.setSubToolbox
}

type FixmethodToolboxProps = FixmethodToolboxStateProps & FixmethodToolboxDispatchProps & ToolboxStripItemProps

const FixmethodToolbox: React.FC<FixmethodToolboxProps> = ({
  sign,
  signType,
  signState,
  data,
  subToolbox,
  setSubToolbox,
}) => {
  const { t } = useTranslation()
  const currentMedia = subToolbox ? media(signType, subToolbox) : null
  const presetMatch = currentPresetSettings(signState, data)
  const adminFixmethods = ClientData.admin ? data.fixmethods.filter(fixmethod => fixmethod.filters?.admin) : []

  return (
    <Toolbox tKey="fixmethod">
      <Scrollable scrollToActive scrollActiveToolboxDelay>
        {data.fixmethods.map(fixmethod => {
          const active = signState.fixmethod === fixmethod.name
          const { image, video, text } = media(signType, fixmethod.name)
          const hasMedia = !!image || !!video || !!text

          if (fixmethod.filters) {
            if (fixmethod.filters.admin) {
              return null
            }

            if ((fixmethod.filters.hidden && !active) || (fixmethod.filters.legacy && !active)) {
              return null
            }

            if (fixmethod.filters.hide_shapes?.includes(signState.shape)) {
              return null
            }
          }

          if (fixmethod.disabled_reason && fixmethod.disabled_reason.includes("locale")) {
            return null
          }

          if (presetMatch && presetMatch.fixmethods) {
            if (!presetMatch.fixmethods.map(method => method.name).includes(fixmethod.name)) {
              return null
            }
          }

          let enabled = fixmethod.enabled
          let forcedState: Partial<SignState> = {}

          if (
            !enabled &&
            fixmethod.forced_settings &&
            fixmethod.disabled_reason?.every(r => fixmethod.forced_settings?.includes(r))
          ) {
            enabled = true
            forcedState = optionForcedState(fixmethod, signState, fixmethod.forced_settings)
          }

          if (fixmethod.name === "table_stand") {
            forcedState.shape = "rect"
          }

          return (
            <Fixmethod
              key={fixmethod.name}
              name={fixmethod.name}
              description={t(description(fixmethod.name, signType, signState.material))}
              active={active}
              enabled={enabled || false}
              disabledReason={(fixmethod.disabled_reason && fixmethod.disabled_reason[0]) || ""}
              update={() => {
                if (!enabled) {
                  return
                }

                sign.updateState({ fixmethod: fixmethod.name, ...forcedState })

                if (!hasMedia) {
                  setSubToolbox(null)
                } else if (subToolbox && subToolbox !== fixmethod.name) {
                  setSubToolbox(fixmethod.name)
                }
              }}
              image={!!image}
              video={!!video}
              text={!!text}
              onReadMore={e => {
                e.stopPropagation()
                e.preventDefault()

                setSubToolbox(fixmethod.name)
              }}
            />
          )
        })}
        {adminFixmethods.length > 0 && (
          <>
            <div className="px-4 py-2">
              <div className="border-t-4 border-t-ink-85">SignAdmin users only</div>
            </div>
            {adminFixmethods.map(adminFixmethod => {
              const active = signState.fixmethod === adminFixmethod.name
              const { image, video, text } = media(signType, adminFixmethod.name)
              const hasMedia = !!image || !!video || !!text

              if (adminFixmethod.filters) {
                if ((adminFixmethod.filters.hidden && !active) || (adminFixmethod.filters.legacy && !active)) {
                  return null
                }

                if (adminFixmethod.filters.hide_shapes?.includes(signState.shape)) {
                  return null
                }
              }

              if (adminFixmethod.disabled_reason && adminFixmethod.disabled_reason.includes("locale")) {
                return null
              }

              if (presetMatch && presetMatch.fixmethods) {
                if (!presetMatch.fixmethods.map(method => method.name).includes(adminFixmethod.name)) {
                  return null
                }
              }

              let enabled = adminFixmethod.enabled
              let forcedState: Partial<SignState> = {}

              if (
                !enabled &&
                adminFixmethod.forced_settings &&
                adminFixmethod.disabled_reason?.every(r => adminFixmethod.forced_settings?.includes(r))
              ) {
                enabled = true
                forcedState = optionForcedState(adminFixmethod, signState, adminFixmethod.forced_settings)
              }

              if (adminFixmethod.name === "table_stand") {
                forcedState.shape = "rect"
              }

              return (
                <Fixmethod
                  key={adminFixmethod.name}
                  name={adminFixmethod.name}
                  description={t(description(adminFixmethod.name, signType, signState.material))}
                  active={active}
                  enabled={enabled || false}
                  disabledReason={(adminFixmethod.disabled_reason && adminFixmethod.disabled_reason[0]) || ""}
                  update={() => {
                    if (!enabled) {
                      return
                    }

                    sign.updateState({ fixmethod: adminFixmethod.name, ...forcedState })

                    if (!hasMedia) {
                      setSubToolbox(null)
                    } else if (subToolbox && subToolbox !== adminFixmethod.name) {
                      setSubToolbox(adminFixmethod.name)
                    }
                  }}
                  image={!!image}
                  video={!!video}
                  text={!!text}
                  onReadMore={e => {
                    e.stopPropagation()
                    e.preventDefault()

                    setSubToolbox(adminFixmethod.name)
                  }}
                />
              )
            })}
          </>
        )}
      </Scrollable>
      {currentMedia &&
        subToolbox &&
        (isTablet() ? (
          <Scrollable>
            <FixmethodDetails image={currentMedia.image} video={currentMedia.video} text={currentMedia.text} />
          </Scrollable>
        ) : (
          <FixmethodDetails image={currentMedia.image} video={currentMedia.video} text={currentMedia.text} />
        ))}
    </Toolbox>
  )
}

export default connect(
  ({ signState, currentSignData, subToolbox, signType }: State) => ({
    signState,
    data: currentSignData,
    subToolbox,
    signType,
  }),
  {
    setSubToolbox: actions.setSubToolbox,
  }
)(FixmethodToolbox)
